import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Star, ExternalLink } from 'lucide-react';
import { SeoMeta } from '../components/seo/SeoMeta';

const BlogPost14: React.FC = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');

  const handleGenerateName = (e: React.FormEvent) => {
    e.preventDefault();
    if (firstName.trim() && surname.trim()) {
      navigate('/chinese-name-generator', { 
        state: { 
          firstName: firstName.trim(),
          surname: surname.trim(),
          autoGenerate: true 
        } 
      });
    }
  };

  return (
    <>
      <SeoMeta
        title="My Name in Chinese - Complete Translation Guide 2025"
        description="Wondering what your name is in Chinese? Learn how to translate your English name using phonetic matching, meaning translation, or cultural adaptation. Discover why 'John' becomes 约翰 (Yuēhàn) or 文杰 (Wénjié), and 'Sarah' becomes 莎拉 (Shālā) or 雅文 (Yǎwén). Step-by-step guide with examples, pronunciation tips, and how to use our AI generator. Get your authentic Chinese name today."
        keywords="my name in chinese, my name is in chinese, what is my chinese name, my chinese name, translate my name to chinese, english name to chinese, convert name to chinese, find my chinese name"
        pathname="/blog/my-name-in-chinese"
      />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50">
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium">
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </nav>

        <header className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4" />
              Translation Guide
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              My Name in Chinese: Complete Translation Guide
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Learn how to translate your English name into Chinese. This comprehensive guide covers phonetic translation, meaning-based names, and cultural considerations for choosing your perfect Chinese name.
            </p>
          </div>
        </header>

        <article className="max-w-4xl mx-auto px-4 pb-16">
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Translate Your Name to Chinese</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              There are several approaches to translating your English name into Chinese. The most common methods include phonetic translation (音译) and meaning-based selection (意译). Understanding these methods will help you find the perfect <strong>Chinese name</strong> that represents you.
            </p>
            <div className="space-y-6">
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Phonetic Translation (音译)</h3>
                <p className="text-gray-700 mb-3">
                  This method matches the sounds of your English name with similar-sounding Chinese characters. For example, "Michael" becomes "迈克尔" (Màikè'ěr). This approach preserves the original sound of your name while adapting it to Chinese phonetics.
                </p>
                <p className="text-gray-700">
                  Phonetic translation is ideal when you want your <strong>Chinese name</strong> to sound similar to your original name, making it easier for people who know you by your English name to recognize your Chinese name.
                </p>
              </div>
              <div className="border-l-4 border-purple-500 pl-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Meaning-Based Selection (意译)</h3>
                <p className="text-gray-700 mb-3">
                  This approach chooses Chinese characters based on the meaning of your English name. For example, "Grace" might become "雅" (Yǎ) meaning elegant. This method creates a name that reflects the essence of your original name.
                </p>
                <p className="text-gray-700">
                  Meaning-based selection is perfect when you want your <strong>Chinese name</strong> to convey the same positive qualities as your English name, creating a deeper cultural connection.
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Combined Approach</h3>
                <p className="text-gray-700">
                  Many people choose a combination approach, selecting Chinese characters that both sound similar to their English name and carry meaningful significance. This creates a <strong>Chinese name</strong> that is both recognizable and culturally rich.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Step-by-Step Translation Process</h2>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Analyze Your English Name</h3>
                  <p className="text-gray-700">Break down your name into syllables and identify its meaning or origin. This helps determine which translation method works best.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose Translation Method</h3>
                  <p className="text-gray-700">Decide whether phonetic translation, meaning-based selection, or a combination works best for your name.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Chinese Characters</h3>
                  <p className="text-gray-700">Choose characters that match your criteria while ensuring they create a harmonious and meaningful name.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Verify Cultural Appropriateness</h3>
                  <p className="text-gray-700">Ensure your chosen <strong>Chinese name</strong> is culturally appropriate and doesn't have unintended negative connotations.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Common Translation Examples</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">English Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Chinese Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pinyin</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">Michael</td>
                    <td className="px-6 py-4 whitespace-nowrap">迈克尔</td>
                    <td className="px-6 py-4 whitespace-nowrap">Màikè'ěr</td>
                    <td className="px-6 py-4 whitespace-nowrap">Phonetic</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">Grace</td>
                    <td className="px-6 py-4 whitespace-nowrap">雅</td>
                    <td className="px-6 py-4 whitespace-nowrap">Yǎ</td>
                    <td className="px-6 py-4 whitespace-nowrap">Meaning</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">David</td>
                    <td className="px-6 py-4 whitespace-nowrap">大卫</td>
                    <td className="px-6 py-4 whitespace-nowrap">Dàwèi</td>
                    <td className="px-6 py-4 whitespace-nowrap">Phonetic</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Get Your Chinese Name Instantly</h2>
            <p className="text-gray-700 mb-6">
              Use our AI-powered Chinese name generator to translate your name instantly. Get authentic Chinese names with meanings, pinyin, and cultural explanations.
            </p>
            <form onSubmit={handleGenerateName} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  required
                />
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="Last Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 transition-colors font-medium"
              >
                Translate My Name to Chinese →
              </button>
            </form>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Resources</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/blog/chinese-name-translation" className="text-purple-600 hover:underline font-medium">
                  Chinese Name Translation: How It Works →
                </Link>
              </li>
              <li>
                <Link to="/blog/how-to-choose-chinese-name" className="text-purple-600 hover:underline font-medium">
                  How to Choose a Chinese Name →
                </Link>
              </li>
              <li>
                <Link to="/" className="text-purple-600 hover:underline font-medium">
                  Chinese Name Generator →
                </Link>
              </li>
            </ul>
          </div>
        </article>
      </div>
    </>
  );
};

export default BlogPost14;

